<template>
  <div
    v-click-outside="emitClose"
    class="sf-dropdown2 tw-rounded-lg tw-content-center"
    :class="dropdownClasses"
    data-testid="dropdown"
    v-bind="$attrs"
    @click.stop.prevent="emitClicked"
  >
    <div :class="selectWrapperClassnames" data-testid="dropdown-wrapper">
      <div
        class="sf-dropdown2-select tw-rounded-lg"
        :class="selectClassNames"
        data-testid="dropdown-select"
        @click="emitTriggerClicked"
      >
        <slot name="trigger" />
      </div>
    </div>
    <div :class="dropdownMenuClassnames">
      <slot name="menu" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  variants,
  drawerSizes,
  triggerSizes,
  drawerDirections,
} from './constants'
import { computed } from 'vue'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfDropdownMenu',
})

interface DropdownProps {
  dropdownId: string
  isFullWidth?: boolean
  isDisabled?: boolean
  isOpen?: boolean
  noOptionsStyling?: boolean
  triggerSize?: string
  drawerSize?: string
  variant?: string
  alignOption?: string
  drawerDirection?: string
  isInsideOtherInput?: boolean
  unstyledTrigger?: boolean
}

const props = withDefaults(defineProps<DropdownProps>(), {
  variant: variants.neutral,
  drawerSize: drawerSizes.default,
  optionPosition: 'static',
  triggerSize: triggerSizes.default,
  drawerDirection: drawerDirections.right,
  alignOption: 'l',
})

const emit =
  defineEmits<{
    'dropdown-closed': [dropdownId: string]
    'dropdown-clicked': [dropdownId: string]
    'dropdown-trigger-clicked': [dropdownId: string]
  }>()

const selectClassNames = computed(() => {
  return {
    'sf-dropdown2-select-padding-small':
      props.triggerSize === triggerSizes.small && !props.unstyledTrigger,
    'tw-p-0': props.unstyledTrigger,
    [`sf-dropdown2-select--${props.variant}`]: props.variant,
    'tw-py-1.5': props.isInsideOtherInput,
    'tw-py-[9px]': !props.isInsideOtherInput,
  }
})

const selectWrapperClassnames = computed(() => {
  return {
    'sf-dropdown2-wrapper': !props.unstyledTrigger,
    'sf-dropdown2-wrapper--open': props.isOpen,
    [`sf-dropdown2-wrapper--${props.variant}`]: props.variant,
    'tw-w-full': props.isFullWidth,
  }
})

const dropdownClasses = computed(() => {
  return {
    'sf-disabled': props.isDisabled,
    [`sf-dropdown2-${props.variant}`]: props.variant,
    [`sf-align-${props.alignOption}`]: props.alignOption,
    'tw-w-full': props.isFullWidth,
  }
})

const dropdownMenuClassnames = computed(() => {
  return {
    'sf-dropdown2-menu': !props.noOptionsStyling,
    'tw-mt-2': !props.noOptionsStyling,
    'sf-dropdown2-menu--open': props.isOpen,
    'sf-dropdown2-menu--large': props.drawerSize === drawerSizes.large,
    'tw-fixed': props.optionPosition === 'fixed',
    [`open-drawer-${props.drawerDirection}`]: props.drawerDirection,
  }
})

const emitClose = () => {
  if (props.isOpen && !props.isDisabled) {
    emit('dropdown-closed', props.dropdownId)
  }
}

const emitClicked = () => {
  if (!props.isDisabled) {
    emit('dropdown-clicked', props.dropdownId)
  }
}

const emitTriggerClicked = () => {
  if (!props.isDisabled) {
    emit('dropdown-trigger-clicked', props.dropdownId)
  }
}
</script>
<style>
.sf-dropdown2-select-padding-small {
  padding: 2px 26px 2px 8px;
}

.open-drawer-left {
  right: 0;
}

.open-drawer-right {
  left: 0;
}
</style>
